import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import {
  ClientStore,
  StoreCatalog,
  Order,
  OrderItem,
  Offer,
  LocationValid,
  EmailMessage,
  AssociatedZone,
  CustomerLoyaltyTrasaction,
  CustomerLoyaltyProfileUpdateData,
  CustomerLoyaltyProfileUpdate,
  ReorderResponse,
  CustomerAssignedVoucher,
} from './../../stores/stores';
import { Injectable } from '@angular/core';
import { Page } from 'src/app/api/types/Page';
import { Reservation } from 'src/app/stores/stores-reservations/reservation-models/reservation';
import { environment } from 'src/environments/environment';
import { AddReservationRequest } from '../../stores/stores-reservations/reservation-models/AddReservationRequest';
import { UpdateReservationRequest } from '../../stores/stores-reservations/reservation-models/UpdateReservationRequest';
import { HelperService } from '../helper.service';
import { CatalogLanguagesList } from './+state/stores.reducer';
import { AvailableSlotsResponse } from './types/AvailableSlotsResponse';
import {
  CustomerAccountActivationRequest,
  CustomerAddress,
  CustomerCheckMembershipResponse,
  CustomerDetailsUpdateRequest,
  CustomerProfile,
  CustomerProfileUpdateRequest,
  CustomerSocialLoginResponse,
  SocialAccountLoginDetails,
  Tokens,
} from './types/CustomerSocialLogin';
import { OrderUpdateRequest } from './types/OrderUpdateRequest';
import {
  UpdateStandalonePaymentItemsRequest,
  UpdateStandalonePaymentRequest,
} from './types/StandalonePayment';
import { UpdateOrderItemQuantityRequest } from './types/UpdateOrderItemQuantityRequest';
import { CustomerVoucher } from './types/VoucherValidationResponse';
import { AddBookingTierRequest } from 'src/app/stores/events/event-models/AddBookingTierRequest';
import {
  AdminAttendeeDTO,
  AttendeeSearchFilters,
  AttendeeSort,
  BookingTierDTO,
  EventDetailsDTO,
  EventDetailsResponse,
  EventVoucherResponse,
} from 'src/app/stores/store-events-bookings/store-events-bookings';
import { PageableResults, Paging } from '../../api/types/Pageable';
import StoreUtils from './utils/StoreUtils';
import { DishSearchRequest } from './types/DishSearchRequest';
import { LocationService } from '../location.service';
import { CUSTOMER_LOGOUT } from 'src/app/auth/token-helper';
import { PreviousOrderLookupResponse } from './types/PreviousOrderLookupResponse';
import { ResolveParentOfferResponse } from './types/ResolveParentOfferResponse';
import { ResolveParentCategoryResponse } from './types/ResolveParentCategoryResponse';
import { PromotionData } from './types/PromotionData';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  constructor(
    private http: HttpClient,
    private helperService: HelperService,
    private locationService: LocationService,
  ) {}

  load(
    storeAlias: string,
    salesPointExternalId?: string,
  ): Observable<ClientStore> {
    let params = new HttpParams();
    if (salesPointExternalId) {
      params = params.set('salespoint', salesPointExternalId);
    }
    return this.http.get<ClientStore>(`/api/v1/stores/${storeAlias}`, {
      params,
    });
  }

  getcatalog(
    storeId: number,
    selectedLang: string,
    referenceTime?: string,
    level: 'PARENT' | 'FULL' = 'PARENT',
  ): Observable<StoreCatalog> {
    const params: any = { level };
    if (selectedLang) {
      params.languageId = selectedLang;
    }
    if (referenceTime) {
      params.referenceTime = referenceTime;
    }
    return this.http.get<StoreCatalog>(`/api/v1/stores/${storeId}/catalog`, {
      params,
    });
  }

  getAvailableLanguages(storeId: number): Observable<CatalogLanguagesList> {
    return this.http.get<CatalogLanguagesList>(
      `/api/v1/stores/${storeId}/catalog/languages`,
    );
  }

  retrieveOfferItem(
    storeId: number,
    catalogId: number,
    offerId: number,
    selectedLang: string = 'en',
    referenceTime?: string,
  ): Observable<Offer> {
    return this.http.get<Offer>(
      `api/v1/stores/${storeId}/catalog/${catalogId}/offer/${offerId}?languageId=${selectedLang}&${referenceTime ? '&referenceTime=' + referenceTime : ''}`,
    );
  }

  /**
   * Card #11620 — read the one promotion a customer tapped in a push
   * notification. The store payload only carries the promotion that is
   * eligible right now, and a store can have several at once, so it cannot be
   * relied on to show the promotion that was actually pushed.
   *
   * 404 when the promotion has ended, was deleted or was deactivated since the
   * push went out; the caller then just opens the store.
   */
  retrievePublicPromotion(
    storeId: number,
    promotionId: number,
  ): Observable<PromotionData> {
    return this.http.get<PromotionData>(
      `/api/v1/stores/${storeId}/promotions/${promotionId}/public`,
    );
  }

  /**
   * Story #10478 - resolves a parent store's shared item deep link to the chosen
   * branch's own offer. Returns `{ carried, offerId, catalogId }`; `carried:false`
   * means the branch does not carry the item (graceful fallback, no error).
   */
  resolveParentOffer(
    childStoreId: number,
    parentOfferId: number,
    parentStoreId: number,
  ): Observable<ResolveParentOfferResponse> {
    const params = new HttpParams().set(
      'parentStoreId',
      parentStoreId.toString(),
    );
    return this.http.get<ResolveParentOfferResponse>(
      `/api/v1/stores/${childStoreId}/catalog/parent-offer/${parentOfferId}/resolve`,
      { params },
    );
  }

  /**
   * Story #11520 - the category twin of {@link resolveParentOffer}: resolves a parent
   * store's shared category deep link to the chosen branch's own category. Returns
   * `{ carried, categoryId, catalogId }`; `carried:false` means the branch does not
   * carry the category (silent fallback to the branch menu, no error).
   */
  resolveParentCategory(
    childStoreId: number,
    parentCategoryId: number,
    parentStoreId: number,
  ): Observable<ResolveParentCategoryResponse> {
    const params = new HttpParams().set(
      'parentStoreId',
      parentStoreId.toString(),
    );
    return this.http.get<ResolveParentCategoryResponse>(
      `/api/v1/stores/${childStoreId}/catalog/parent-category/${parentCategoryId}/resolve`,
      { params },
    );
  }

  initializeEmptyOrder(storeId: number, payload: Order): Observable<Order> {
    if (this.isOrderCapture()) {
      return this.http.post<Order>(
        `api/v2/user/stores/${storeId}/orders`,
        payload,
      );
    }
    return this.http.post<Order>(`api/v1/stores/${storeId}/orders`, payload);
  }

  reorder(
    storeId: number,
    orderUuid: string,
    targetOrderUuid?: string,
    wishTime?: string,
  ): Observable<ReorderResponse> {
    let params = new HttpParams();
    if (targetOrderUuid) {
      params = params.set('targetOrderUuid', targetOrderUuid);
    }
    if (wishTime) {
      params = params.set('wishTime', wishTime);
    }

    return this.http.post<ReorderResponse>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/reorder`,
      null,
      { params },
    );
  }

  checkExistingOrder(
    storeId: number,
    orderUuid: string,
    locale: string = 'en',
    simplified: boolean,
  ): Observable<Order> {
    return this.http.get<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}?locale=${locale}&simplified=${simplified}`,
    );
  }

  lookupPreviousOrder(
    storeId: number,
    orderToken: string,
  ): Observable<PreviousOrderLookupResponse> {
    const params = new HttpParams().set('orderToken', orderToken);
    return this.http.get<PreviousOrderLookupResponse>(
      `/api/v1/stores/${storeId}/orders/lookup`,
      { params },
    );
  }

  addOrderItem(
    storeId: number,
    orderUuid: string,
    orderItem: OrderItem,
  ): Observable<Order> {
    return this.http.put<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems`,
      orderItem,
    );
  }

  // for future use, expected to be able to bulk add offers to cart
  addOrderItems(
    storeId: number,
    orderUuid: string,
    orderItems: OrderItem[],
  ): Observable<Order> {
    return this.http.put<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems`,
      orderItems,
    );
  }

  updateOrderItem(
    storeId: number,
    orderUuid: string,
    itemUuid: string,
    orderItem: OrderItem,
  ): Observable<OrderItem> {
    return this.http.put<OrderItem>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems/${itemUuid}`,
      orderItem,
    );
  }

  removeOrderItem(
    storeId: number,
    orderUuid: string,
    itemUuid: string,
  ): Observable<Order> {
    return this.http.delete<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems/${itemUuid}`,
    );
  }

  isAdminOrderUpdate(): boolean {
    return window.location.href.includes('/orders/');
  }

  isOrderCapture(): boolean {
    return window.location.href.includes('/capture/');
  }

  orderUpdate(
    storeId: number,
    orderUuid: string,
    payload: OrderUpdateRequest,
    v2Support: boolean,
  ): Observable<Order> {
    let headers = new HttpHeaders();

    if (this.helperService.isMobileApp()) {
      headers = headers.set('x-referer', window.location.href);
    }

    const isAdminOrderUpdate = this.isAdminOrderUpdate();

    // Check for printer query parameter in URL (or cached value after URL cleanup)
    let params = new HttpParams();
    const printerParam =
      this.locationService.readPrinterFromUrl() ||
      this.locationService.getPrinterName(storeId);
    if (printerParam && !isAdminOrderUpdate) {
      params = params.set('printer', printerParam);
    }
    // #11435 Send the cached Sales Point alongside the printer so the backend's
    // bind-on-update can attach it to an order created before the ?salespoint=
    // link resolved. Suppressed for admin updates like the printer, and for
    // Order Capture, where `OrderServiceV2.bindSalesPointOnUpdate` returns early
    // on `isAdminOrderCapture` and would ignore it; `bindPrinterOnUpdate` has no
    // such exemption, so the printer is still sent there.
    //
    // The printer cookie is keyed by store id, the Sales Point cookie by store
    // alias, and `applySalesPointResolution` writes that cookie under the alias
    // the backend validated the external id for. So the alias must be the one
    // of `storeId`, not of whichever store is on screen: on a parent/child
    // storefront they differ, and a sibling's value would miss at best and bind
    // the wrong Sales Point at worst. `selectedStore` is the only id -> alias
    // mapping available here, so it is used only when it is the requested
    // store; otherwise (including before any store has resolved, on a hard
    // refresh or deep link) nothing is sent, same as when no cookie is set.
    const selectedStore = this.locationService.selectedStore;
    const aliasForRequestedStore =
      selectedStore?.id === storeId ? selectedStore.aliasName : null;
    const salesPointExternalId = aliasForRequestedStore
      ? this.locationService.getSalesPointExternalId(aliasForRequestedStore)
      : null;
    if (salesPointExternalId && !isAdminOrderUpdate && !this.isOrderCapture()) {
      params = params.set('salespoint', salesPointExternalId);
    }

    if (isAdminOrderUpdate) {
      if (payload.validateOrder) {
        delete payload.validateOrder;
        delete payload.status;
      }
      payload.validateOrder = undefined;
      payload.status = undefined;

      // Remove captchaToken for admin updates
      if (payload.captchaToken) {
        delete payload.captchaToken;
      }

      return this.http.patch<Order>(
        `api/v2/user/stores/${storeId}/orders/${orderUuid}`,
        payload,
        {
          headers: headers,
          params: params,
        },
      );
    }
    if (v2Support) {
      return this.http.put<Order>(
        `api/v2/stores/${storeId}/orders/${orderUuid}`,
        payload,
        {
          headers: headers,
          params: params,
        },
      );
    } else {
      return this.http.put<Order>(
        `api/v1/stores/${storeId}/orders/${orderUuid}`,
        payload,
        {
          params: params,
        },
      );
    }
  }

  orderUpdateStatus(
    storeId: number,
    orderUuid: string,
    orderStatus: string,
  ): Observable<Order> {
    const httpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    });
    return this.http.put<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/status`,
      JSON.stringify(orderStatus),
      {
        headers: httpHeaders,
      },
    );
  }

  orderUpdateQuantities(
    storeId: number,
    orderUuid: string,
    payload: UpdateOrderItemQuantityRequest[],
  ): Observable<void> {
    return this.http.put<void>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems/actions/updateQuantities`,
      payload,
    );
  }

  validateStoreLocation(
    storeId: number,
    storeLocation: string,
  ): Observable<LocationValid> {
    return this.http.post<LocationValid>(
      `api/v1/stores/${storeId}/locations/validate`,
      {
        label: storeLocation,
      },
    );
  }

  sendOrderByEmail(orderUuid: string, email: string): Observable<EmailMessage> {
    return this.http.post<EmailMessage>(
      `api/v1/stores/order/${orderUuid}/email`,
      { email },
    );
  }

  sendStandaloneOrderByEmail(
    storeId: string,
    standalonePaymentId: string,
    email: string,
  ) {
    return this.http.post<EmailMessage>(
      `api/v1/stores/${storeId}/standalone-payment/${standalonePaymentId}/email`,
      { email },
    );
  }

  getZonePerZipcode(storeId: number, zipCode: string): Observable<any> {
    return this.http.get<any>(
      `api/v1/stores/${storeId}/zones?postcode=${zipCode}`,
    );
  }

  /**
   * The store's ordering rules with the reward groups available to them.
   *
   * `referenceTime` (ISO-8601 instant) asks the backend to evaluate group and
   * option schedules at that moment - the order's wish time - rather than at
   * the start of `referenceDate`, which older clients keep sending (#11062).
   */
  getStoreRules(
    storeId: number,
    langId: string,
    referenceDate: string,
    referenceTime?: string,
  ): Observable<any> {
    let params = new HttpParams();
    if (langId) params = params.set('languageId', langId);
    if (referenceDate) params = params.set('referenceDate', referenceDate);
    if (referenceTime) params = params.set('referenceTime', referenceTime);

    return this.http.get<any>(`api/v1/stores/${storeId}/rules`, { params });
  }

  validateVoucher(
    storeId: number,
    voucherCode: string,
  ): Observable<CustomerVoucher> {
    return this.http.post<CustomerVoucher>(
      `api/v1/stores/${storeId}/customer-voucher/validate`,
      {
        code: voucherCode,
      },
    );
  }

  getSlots(
    storeId: number,
    deliveryMode: string,
    date?: string,
  ): Observable<AvailableSlotsResponse> {
    return this.http.post<AvailableSlotsResponse>(
      `api/v1/stores/${storeId}/slots`,
      {
        date,
        mode: deliveryMode,
      },
    );
  }

  customerRegistrationV2(
    email: string,
    captchaToken: string,
    storeId?: number,
  ): Observable<any> {
    return this.http.post<any>(`api/v2/auth/register/customer`, {
      email,
      captchaToken,
      storeId,
    });
  }

  customerActivation(
    email: string,
    code: string,
    storeId?: number,
  ): Observable<any> {
    return this.http.post<any>(`api/v1/auth/activate/customer`, {
      email,
      code,
      storeId,
    });
  }

  customerPasswordUpdateByCode(
    code: string,
    newPassword: string,
  ): Observable<any> {
    return this.http.post<any>(`api/v1/auth/profile/password/update-by-code`, {
      code,
      newPassword,
    });
  }

  customerResetByCode(email: string, storeId?: number): Observable<any> {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8',
    );
    // The body is the bare email, so the store goes in the query string.
    const params = storeId ? { storeId: String(storeId) } : undefined;
    return this.http.post<any>(`api/v1/auth/password/reset-by-code`, email, {
      headers,
      params,
    });
  }

  socialLogin(
    loginRequest: SocialAccountLoginDetails,
  ): Observable<CustomerSocialLoginResponse> {
    return this.http.post<CustomerSocialLoginResponse>(
      `/api/v1/auth/login/customer`,
      loginRequest,
    );
  }

  /**
   * `storeId` is the storefront the session is used on (#10368): the backend refuses to refresh a
   * store's own account at a store outside the group that owns it.
   */
  refreshToken(refreshToken: string, storeId?: number): Observable<Tokens> {
    const query = storeId ? `?storeId=${storeId}` : '';
    return this.http.post<Tokens>(
      `/api/v1/auth/token/refresh${query}`,
      refreshToken,
    );
  }

  /**
   * Revokes every refresh token of the signed-in customer, on every site and
   * device. Marked {@link CUSTOMER_LOGOUT} so it carries the customer's JWT even
   * where a backoffice session shares the browser (admin order capture).
   * A `jwt` given here (a fresh one, for the retry after an expired JWT) is
   * sent instead of the stored one: `CustomerTokenInterceptor` leaves a request
   * that already carries a JWT alone.
   */
  customerLogout(jwt?: string): Observable<Record<string, never>> {
    return this.http.post<Record<string, never>>(
      '/api/v1/auth/logout',
      {},
      {
        context: new HttpContext().set(CUSTOMER_LOGOUT, true),
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
      },
    );
  }

  customerCheckMembership(
    storeId: number,
  ): Observable<CustomerCheckMembershipResponse> {
    return this.http.post<CustomerCheckMembershipResponse>(
      `/api/v1/auth/check-membership/customer`,
      { storeId },
    );
  }

  customerDetailsUpdate(
    customerDetailsUpdateRequest: CustomerDetailsUpdateRequest,
  ): Observable<CustomerSocialLoginResponse> {
    return this.http.post<CustomerSocialLoginResponse>(
      `/api/v1/auth/update/customer`,
      customerDetailsUpdateRequest,
    );
  }

  getCustomerLoyalty(storeId: number): Observable<any> {
    return this.http.get<any>(`api/v1/stores/${storeId}/loyalty`);
  }

  getCustomerLoyaltyTransactions(
    storeId: number,
    paging: Paging,
  ): Observable<Page<CustomerLoyaltyTrasaction>> {
    let params = new HttpParams();
    params = params.append('page', paging?.page?.toString());
    params = params.append('size', paging?.size?.toString());
    return this.http.get<Page<CustomerLoyaltyTrasaction>>(
      `api/v1/stores/${storeId}/loyalty/transactions`,
      { params },
    );
  }

  getCustomerProfile(): Observable<CustomerProfile> {
    return this.http.get<CustomerProfile>(`/api/v1/users/customer-profile`);
  }

  updateCustomerProfile(
    request: CustomerProfileUpdateRequest,
  ): Observable<CustomerProfile> {
    return this.http.put<CustomerProfile>(
      `/api/v1/users/customer-profile`,
      request,
    );
  }

  deleteCustomerProfile(userId: number): Observable<any> {
    return this.http.delete<any>(`/api/v1/users/${userId}`);
  }

  updateCustomerAddress(
    request: CustomerAddress,
    storeId?: number,
    isAdminUi?: boolean,
  ): Observable<CustomerProfile> {
    if (isAdminUi) {
      return this.http.post<CustomerProfile>(
        `/api/v1/stores/${storeId}/customers/customer-address`,
        request,
      );
    }
    return this.http.post<CustomerProfile>(
      `/api/v1/users/customer-address`,
      request,
    );
  }

  deleteCustomerAddress(
    addressId: number,
    customerId?: number,
    storeId?: number,
  ): Observable<CustomerProfile> {
    if (customerId && storeId) {
      return this.http.delete<CustomerProfile>(
        `/api/v1/stores/${storeId}/customers/customer-address`,
        {
          body: { id: addressId, customerId },
        },
      );
    }
    return this.http.delete<CustomerProfile>(
      `/api/v1/users/customer-address/${addressId}`,
    );
  }

  uploadCustomerProfilePicture(file: File): Observable<CustomerProfile> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<CustomerProfile>(
      `/api/v1/users/profile-picture`,
      formData,
    );
  }

  deleteCustomerProfilePicture(): Observable<CustomerProfile> {
    return this.http.delete<CustomerProfile>(`/api/v1/users/profile-picture`);
  }

  getLocationFromAddress(
    address: string,
    zipCode: string | null | undefined,
    countryCode: string,
  ): Observable<any> {
    if (StoreUtils.isMapsDisabled()) {
      return from(
        new Promise((resolve) => {
          resolve(null);
        }),
      );
    }
    const geocoder = new google.maps.Geocoder();
    // Postcode-hidden stores pass a null/undefined zip; keep it out of the
    // query string and normalize it safely so a throw inside the geocoder
    // callback can't leave the promise unsettled and hang the observable.
    const zipPart = zipCode ? ` ${zipCode},` : '';
    const addressString = `${address},${zipPart} ${countryCode}`;
    const requestedZip = (zipCode ?? '').replace(/\s/g, '').toLowerCase();

    const geocodePromise = new Promise((resolve) => {
      geocoder.geocode({ address: addressString }, (results, status) => {
        if (status === 'OK' && results[0]) {
          // Reject when Google "corrected" the address to a postcode other
          // than the one the customer typed — those coordinates would compute
          // the zone/fee from the wrong point (#4895). When no zip was
          // requested (Optional postcode left blank, or Hidden), there is
          // nothing to cross-check and street + city carry the resolution on
          // their own — without this bypass every blank-zip geocode resolved
          // null and the zone lookup could never run (#11154). Deliberate
          // trade-off: with no zip an ambiguous street name can resolve to
          // the wrong town and compute the zone/fee from that point, for
          // every Optional-postcode store. Accepted, because the alternative
          // is the permanent Submit block, and a typed zip still cross-checks.
          const postalCode = results[0].address_components.find((component) =>
            component.types.includes('postal_code'),
          );
          if (
            !requestedZip ||
            (postalCode &&
              postalCode.short_name.replace(/\s/g, '').toLowerCase() ===
                requestedZip)
          ) {
            resolve({ results, status });
          } else {
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });

    return from(geocodePromise);
  }

  getAssociatedZone(
    storeId: number,
    orderUuid: string,
  ): Observable<AssociatedZone> {
    return this.http.get<AssociatedZone>(
      `api/v2/stores/${storeId}/orders/${orderUuid}/associatedZone`,
    );
  }

  getSiblingUrl(
    selectedSiblingStoreId,
    selectedSiblingStoreAlias,
    domainUrl,
    adminHostUrl,
    externalDomain,
  ) {
    if (this.isOrderCapture()) {
      return `${adminHostUrl}/manager/stores/${selectedSiblingStoreId}/capture/${selectedSiblingStoreAlias}`;
    }

    if (externalDomain) {
      return externalDomain.startsWith('https://')
        ? externalDomain
        : `https://${externalDomain}`;
    }

    const portSuffix =
      environment.name === 'local' ? `:${window.location.port}` : '';
    const domainWithPort = domainUrl.includes(':')
      ? domainUrl
      : `${domainUrl}${portSuffix}`;
    const protocol =
      window.location.protocol === 'httpsionic:'
        ? 'https:'
        : window.location.protocol;
    return `${protocol}//${selectedSiblingStoreAlias}.${domainWithPort}`;
  }

  getCombinedOrders(storeId: number, locationId: string): Observable<any> {
    return this.http.get<any>(
      `api/v1/stores/${storeId}/location/${locationId}/getCombinedOrders`,
    );
  }

  createStandalonePayment(
    storeId: number,
    createRequest: any,
  ): Observable<any> {
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/standalone-payment`,
      createRequest,
    );
  }

  updateStandalonePayment(
    storeId: number,
    paymentId: string,
    createRequest: UpdateStandalonePaymentRequest,
  ): Observable<any> {
    return this.http.patch<any>(
      `/api/v1/stores/${storeId}/standalone-payment/${paymentId}`,
      createRequest,
    );
  }

  updateStandalonePaymentItems(
    storeId: number,
    paymentId: string,
    createRequest: UpdateStandalonePaymentItemsRequest,
  ): Observable<any> {
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/standalone-payment/${paymentId}/standalonePaymentItems`,
      createRequest,
    );
  }

  customerStandalonePaymentSearch(storeId: number, action: any) {
    let url = '';
    if (action.orderToken) {
      url = `/api/v1/stores/${storeId}/standalone-payment?orderToken=${action.orderToken}`;
    } else {
      url = `/api/v1/stores/${storeId}/standalone-payment?l=${action.locationId}&tapId=${action.tapId}`;
    }
    return this.http.get<any>(url);
  }

  initiateStandalonePayment(
    storeId: number,
    paymentId: string,
    data: any,
  ): Observable<any> {
    let headers = new HttpHeaders();
    // use for payment redirection flow strip flow backward compatible
    headers = headers.set('payment-flow-version', '3');
    return this.http.put<any>(
      `/api/v1/stores/${storeId}/standalone-payment/${paymentId}/initiate`,
      data,
      { headers },
    );
  }

  completeStandalonePayment(
    storeId: number,
    paymentId: string,
    paymentProviderResponse = {},
  ): Observable<any> {
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/standalone-payment/${paymentId}/complete`,
      JSON.stringify(paymentProviderResponse),
    );
  }

  getStandaloneOrderPayment(storeId: number, standalonePaymentId: string) {
    const url = `/api/v1/stores/${storeId}/standalone-payment/${standalonePaymentId}`;
    return this.http.get<any>(url);
  }

  getStoreReservations(
    storeId: string,
    status?: string,
    location?: number,
    name?: string,
    reservationNumber?: string,
    sortCreatedAt?: string,
    sortArrivalTime?: string,
    arrivalDate?: string,
    offset?: number,
    limit?: number,
  ) {
    let params = new HttpParams();
    params = params.set('storeId', storeId);
    if (status) {
      params = params.set('status', status);
    }
    if (location) {
      params = params.set('location', location);
    }
    if (name) {
      params = params.set('name', name);
    }
    if (reservationNumber) {
      params = params.set('reservationNumber', reservationNumber);
    }
    if (sortCreatedAt) {
      params = params.set('sortCreatedAt', sortCreatedAt);
    }
    if (sortArrivalTime) {
      params = params.set('sortArrivalTime', sortArrivalTime);
    }
    if (arrivalDate) {
      params = params.set('arrivalDate', arrivalDate);
    }
    if (offset) {
      params = params.set('offset', offset);
    }
    if (limit) {
      params = params.set('limit', limit);
    }
    const url = `/api/v1/reservation/getAll`;
    return this.http.get<any>(url, { params });
  }
  addNewReservation(addReservationBody: AddReservationRequest) {
    let params = new HttpParams();
    params = params.set('storeId', addReservationBody.storeId);
    const url = `/api/v1/reservation/add`;
    return this.http.post<any>(url, addReservationBody, { params });
  }

  updateReservation(addReservationBody: UpdateReservationRequest) {
    let params = new HttpParams();
    params = params.set('storeId', addReservationBody.storeId);
    const url = `/api/v1/reservation/update`;
    return this.http.put<any>(url, addReservationBody, { params });
  }
  deleteReservation(id: number, storeId: number) {
    let params = new HttpParams();
    params = params.set('id', id);
    params = params.set('storeId', storeId);
    const url = `/api/v1/reservation/delete`;
    return this.http.delete<any>(url, { params });
  }
  getAvailableSlots(
    date: string,
    storeId: number,
    defaultDuration: number,
    numberOfPeople: number,
    slotInterval: number,
    isAdmin: boolean,
  ) {
    let params = new HttpParams();
    params = params.set('storeId', storeId);
    params = params.set('defaultDuration', defaultDuration);
    params = params.set('numberOfPeople', numberOfPeople);
    params = params.set('date', date);
    params = params.set('slotInterval', slotInterval);
    params = params.set('isAdmin', isAdmin);
    const url = `api/v1/reservation/available-slots`;
    return this.http.get<any>(url, { params });
  }
  addNewCustomerReservation(addReservationBody: AddReservationRequest) {
    const url = `/api/v1/reservation/customer-reservation`;
    return this.http.post<any>(url, addReservationBody);
  }

  sendConfirmMail(addReservationBody: Reservation) {
    const url = `/api/v1/reservation/customer-reservation-mail`;
    return this.http.post<any>(url, addReservationBody);
  }

  getReservationTimeline(storeId: number, date: string) {
    const url = `/api/v1/reservation/timeline/${storeId}`;
    let params = new HttpParams();
    params = params.set('date', date);
    return this.http.get<any>(url, { params });
  }

  getReservationStatistics(
    storeId: number,
    fromDate: string,
    toDate: string,
    periodicTerm: string,
  ) {
    const url = `/api/v1/reservation/statistics/${storeId}`;
    let params = new HttpParams();
    params = params.set('fromDate', fromDate);
    params = params.set('toDate', toDate);
    params = params.set('periodicTerm', periodicTerm);
    return this.http.get<any>(url, { params });
  }

  getEventsByVenueId(storeId, status) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events?status=${status}`;
    return this.http.get<any>(url);
  }

  getEventById(eventId: number, storeId: number): Observable<EventDetailsDTO> {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}`;
    return this.http.get<EventDetailsDTO>(url);
  }
  /**
   * Paged attendee search for one event on the XO events microservice. The store
   * id is the venue id.
   *
   * `eventId` must be the *effective* event id: the query matches
   * `b.event.id` exactly and does not walk into an event's children, so a parent
   * event returns only its own bookings and a sub-event must be passed by its own
   * id.
   *
   * Only the single-attendee endpoint of this API is deliberately not wrapped:
   * the expanded row renders from the object the list already holds, so opening
   * it issues no second request and a wrapper would have no caller.
   */
  searchEventAttendees(
    storeId: number,
    eventId: number,
    filters: AttendeeSearchFilters = {},
    paging?: Paging | null,
    sort?: AttendeeSort | null,
  ): Observable<Page<AdminAttendeeDTO>> {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}/attendees`;
    let params = new HttpParams();

    if (filters.search) {
      params = params.set('search', filters.search);
    }
    // `questionId` and `answer` only mean anything together — the backend
    // ignores a question id with no answer to match it against.
    if (filters.questionId != null && filters.answer) {
      params = params.set('questionId', filters.questionId.toString());
      params = params.set('answer', filters.answer);
    }
    // Repeated once per status. Sending none is NOT "every status": the backend
    // substitutes its own default scope of CONFIRMED + ARRIVED for an empty or
    // absent value, so a caller that wants everything enumerates everything.
    (filters.bookingStatus ?? []).forEach((status) => {
      params = params.append('bookingStatus', status);
    });
    if (filters.occurrenceId != null) {
      params = params.set('occurrenceId', filters.occurrenceId.toString());
    }
    if (paging?.page != null) {
      params = params.set('page', paging.page.toString());
    }
    if (paging?.size != null) {
      params = params.set('size', paging.size.toString());
    }
    if (sort) {
      params = params.set('sort', `${sort.sortBy},${sort.sortDirection}`);
    }

    return this.http.get<Page<AdminAttendeeDTO>>(url, { params });
  }

  /**
   * Every attendee on one booking. The endpoint takes no paging parameters and
   * returns the whole booking as a single page (XO
   * `AdminAttendeeController.getBookingAttendees`, "bounded by booking size"),
   * so one request is the complete list and only its content is passed on.
   */
  getBookingAttendees(
    storeId: number,
    bookingId: number,
  ): Observable<AdminAttendeeDTO[]> {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/bookings/${bookingId}/attendees`;
    return this.http
      .get<Page<AdminAttendeeDTO>>(url)
      .pipe(map((page) => page?.content ?? []));
  }

  updateEventById(storeId, eventId, payload) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}`;
    return this.http.patch<EventDetailsDTO>(url, payload);
  }

  saveEvent(storeId, payload) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events`;
    return this.http.post<EventDetailsResponse>(url, payload);
  }
  getEventVouchers(
    storeId: number,
    eventId: number,
  ): Observable<EventVoucherResponse[]> {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}/vouchers`;
    return this.http.get<EventVoucherResponse[]>(url);
  }

  assignVoucherToEvent(
    storeId: number,
    eventId: number,
    voucherId: number,
  ): Observable<EventVoucherResponse> {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}/vouchers/${voucherId}`;
    return this.http.put<EventVoucherResponse>(url, null);
  }

  deleteVoucherLink(
    storeId: number,
    eventId: number,
    voucherId: number,
  ): Observable<void> {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}/vouchers/${voucherId}`;
    return this.http.delete<void>(url);
  }

  deleteEvent(storeId, eventId) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}`;
    return this.http.delete<any>(url);
  }

  deleteBookingTier(storeId, bookingTierId) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/booking-tiers/${bookingTierId}`;
    return this.http.delete<any>(url);
  }

  saveBooking(storeId, eventId, payload) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}/booking-tiers`;
    return this.http.post<any>(url, payload);
  }

  updateBooking(eventId, payload, bookingId) {
    const url = `${environment.eventsBackendURL}/v1/venues/${eventId}/booking-tiers/${bookingId}`;
    return this.http.put<any>(url, payload);
  }

  saveEventMedia(file, fileType) {
    const formdata = new FormData();
    formdata.append('file', file);
    const url = `${environment.eventsBackendURL}/v1/media?fileType=${fileType}`;
    return this.http.post<any>(url, formdata);
  }
  getReservationTable(
    storeId: number,
    addReservationBody: AddReservationRequest,
  ) {
    const url = `/api/v1/reservation/getTable/${storeId}`;
    return this.http.post<any>(url, addReservationBody);
  }
  getBookingTier() {
    const url = `${environment.eventsBackendURL}/v1/booking-tiers/`;
    return this.http.get<BookingTierDTO>(url);
  }
  addNewBookingTier(addBookingTier: AddBookingTierRequest) {
    const url = `${environment.eventsBackendURL}/v1/booking-tiers`;
    return this.http.post<any>(url, addBookingTier);
  }
  getReturnSlots(storeId: Number, wishDate: string, deliveryMethod: string) {
    let params = new HttpParams();
    if (deliveryMethod != null && deliveryMethod != undefined) {
      params = params.set('deliveryMethod', deliveryMethod);
    }

    const url = `/api/v1/stores/${storeId}/slots/${wishDate}`;
    return this.http.get<string[]>(url, { params });
  }
  setReturnSlots(storeId: Number, orderUuid: string, wishDate: string) {
    const url = `/api/v1/stores/${storeId}/order/${orderUuid}/returnTime`;
    let bodyRequest = { returnTime: wishDate };
    return this.http.post<any>(url, bodyRequest);
  }
  getItemAvailability(
    storeId: number,
    itemId: number,
    referenceTime: string,
  ): Observable<any> {
    const params = new HttpParams().set('referenceTime', referenceTime);
    const url = `/api/v1/stores/${storeId}/item/${itemId}`;
    return this.http.get<any>(url, { params });
  }

  getCustomerOrders(storeId: number, paging: Paging): Observable<Page<Order>> {
    return this.http.get<Page<Order>>(
      `/api/v2/user/stores/${storeId}/orders?page=${paging.page}&size=${paging.size}`,
    );
  }

  getCustomerVouchers(
    storeId: number,
    paging: Paging = { page: 0, size: 5 },
    availableOnly: boolean = false,
  ): Observable<PageableResults<CustomerAssignedVoucher>> {
    const params = new HttpParams()
      .set('page', paging.page.toString())
      .set('size', paging.size.toString())
      .set('availableOnly', availableOnly.toString());

    return this.http.get<PageableResults<CustomerAssignedVoucher>>(
      `/api/v1/user/stores/${storeId}/customer-vouchers`,
      { params },
    );
  }

  getMinDeliveryFeeStore(
    storeIds?: number[],
    postcode?: string,
    latitude?: number,
    longitude?: number,
  ): Observable<number> {
    let params = new HttpParams();
    if (storeIds && storeIds.length) {
      storeIds.forEach((id) => {
        params = params.append('storeIds', String(id));
      });
    }
    if (postcode) {
      params = params.set('postcode', postcode);
    }
    if (latitude !== undefined && latitude !== null) {
      params = params.set('latitude', String(latitude));
    }
    if (longitude !== undefined && longitude !== null) {
      params = params.set('longitude', String(longitude));
    }

    return this.http.get<number>(`/api/v2/stores/min-delivery-fee-store`, {
      params,
    });
  }

  findStoresByDishName(
    searchTerm: string,
    storeIds: number[],
  ): Observable<ClientStore[]> {
    const request: DishSearchRequest = {
      searchTerm,
      storeIds,
    };
    return this.http.post<ClientStore[]>(`/api/v1/stores/search-dish`, request);
  }

  activateCustomerAccount(
    request: CustomerAccountActivationRequest,
  ): Observable<void> {
    return this.http.post<void>(
      `/api/v1/auth/customer/activate-account`,
      request,
    );
  }
}
